import {Endpoint, MakeRequest, RestResponse} from './request'
import {
  AuthType,
  AuthUserResponse,
  CountryModel,
  LocationModel,
  RegistrationType,
  ReviewModel,
  ScheduleDayModel,
  SettingsLanguageModel,
  ToggleSubscribePricePayload,
  ToggleSubscribePriceRequestDTO,
} from '../models'
import type {AppStorage} from '../index'
import type {Secure} from '../helpers/userSecure'
import type {GeoApi} from './geoApi'
import {timestamp} from '../utils/request.utils'
import {
  mapRestCountriesToAppCountries,
  mapRestReviewsToAppReviews,
} from './mappers'

export class OldRest {
  private cache = new Map<string, any>()
  constructor(
    _isDev: boolean,
    _endpoint: string,
    private request: MakeRequest,
    private storage: AppStorage,
    private secure: Secure,
    private geo: GeoApi,
  ) {}

  createUser = async (
    incoming: string,
    name: string,
    surname: string,
    accountType: RegistrationType,
    authType: AuthType,
    pass: string,
    url?: string,
    referLink?: string,
    image?: string,
  ): Promise<RestResponse<number>> => {
    let location: LocationModel | null = this.storage.location
    if (location === null) {
      location = (await this.geo.fetchGPSByIP()).result ?? null
    }
    if (location === null) return {error: 'ERROR_FORM_DATA'}

    const lang = this.storage.language
    const sendParams: any = {
      type_account: accountType,
      refer_link: referLink,
      lang,
      lat: location.latitude,
      lng: location.longitude,
      name,
      surname,
      pass,
      url,
    }

    if (authType === AuthType.phone) {
      sendParams.num = incoming
    } else {
      sendParams.email = incoming
    }

    const file = image !== undefined ? {file: image, name: 'image'} : undefined

    const response = await this.request.request<{id: number}>({
      api: Endpoint.createNewUser,
      body: sendParams,
      file: file,
    })
    // @ts-ignore
    if (response.result?.error) return {error: response.result?.error}
    return {result: response.result?.id}
  }

  /** @deprecated */
  contactSupport = (message: string, appId: 'ios' | 'android') => {
    this.request.request({
      api: 'v1/contact_support.php',
      body: {message, app_id: appId, type: 1},
    })
  }

  activateWithCode = async (
    code: string,
    id: number,
  ): Promise<RestResponse<AuthUserResponse>> => {
    const sendParams: any = {
      app_id: this.storage.platform,
      id,
      code,
    }
    return await this.request.request<AuthUserResponse>({
      api: Endpoint.activeNewUser,
      body: sendParams,
    })
  }

  checkPhoneNumber = async (
    type: AuthType,
    incoming: string,
  ): Promise<RestResponse<String>> => {
    const params = type === AuthType.phone ? {num: incoming} : {email: incoming}
    return this.request.request<string>({
      api: Endpoint.checkUser,
      body: params,
    })
  }

  authWithCode = async (
    phone: string,
    code: string,
  ): Promise<RestResponse<AuthUserResponse>> => {
    const sendParams: any = {
      app_id: this.storage.platform,
      num: phone,
      code,
    }
    return await this.request.request<AuthUserResponse>({
      api: Endpoint.authUser,
      body: sendParams,
    })
  }

  authWithPassword = async (
    email: string,
    password: string,
  ): Promise<RestResponse<AuthUserResponse>> => {
    const sendParams: any = {
      app_id: this.storage.platform,
      pass: password,
      email,
    }
    return await this.request.request<AuthUserResponse>({
      api: Endpoint.authUser,
      body: sendParams,
    })
  }

  resendCode = async (
    type: AuthType,
    incoming: string,
    token?: string,
  ): Promise<RestResponse<number>> => {
    const params = {
      token,
      [type === AuthType.phone ? 'num' : 'email']: incoming,
    }
    return await this.request.request({
      api: Endpoint.resendCode,
      body: params,
    })
  }

  fetchCountries = async (): Promise<Array<CountryModel>> => {
    try {
      const lang = this.storage.language
      const cacheKey = `countries_${lang}`
      if (this.cache.has(cacheKey)) return this.cache.get(cacheKey)
      const response = await this.request.request<any>({
        api: Endpoint.countryList,
        method: 'GET',
        body: {lang},
      })
      const countries = mapRestCountriesToAppCountries(response.result)
      this.cache.set(cacheKey, countries)
      return countries
    } catch (e) {
      console.error(e)
    }
    return []
  }

  fetchLanguages = async (
    lang: string,
  ): Promise<RestResponse<Array<SettingsLanguageModel>>> => {
    const cacheKey = `languages_${lang}`
    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey)
    const response = await this.request.request<Array<SettingsLanguageModel>>({
      api: Endpoint.getLanguages,
      method: 'GET',
      body: {lang},
    })
    const result = {
      error: response.error,
      result: response.result?.map((m, i) => ({
        id: i,
        code: m.code,
        name: m.name,
      })),
    }
    this.cache.set(cacheKey, result)
    return result
  }

  toggleSubscribePrice = async (payload: ToggleSubscribePricePayload) => {
    try {
      const hash = this.storage.userHash!
      const secure = this.secure.createUserSecure()
      const response = await this.request.request<
        any,
        ToggleSubscribePriceRequestDTO
      >({
        api: Endpoint.subscribePrice,
        body: {
          ...payload,
          hash,
          secur: secure,
        },
      })
      if (!response.error) return true
    } catch (e) {
      console.log('restToggleSubscribePrice error => ', e)
    }
    return false
  }

  fetchUserRatings = async (
    userId: string,
    page: number,
  ): Promise<RestResponse<Array<ReviewModel>>> => {
    const hash = this.storage.userHash
    const lang = this.storage.language

    const response = await this.request.request<any>({
      api: Endpoint.userRatings,
      body: {
        id: userId,
        lang: lang,
        timestamp: timestamp(),
        uid: hash,
        app_id: this.storage.platform,
        page: page,
      },
    })
    if (response.error) return response
    const reviews = mapRestReviewsToAppReviews(response.result.author)
    return {...response, result: reviews}
  }

  setScheduleList = async (list: Array<ScheduleDayModel>) => {
    const params = {
      num: '',
      app_id: this.storage.platform,
      hash: this.storage.userHash!,
      oper: 8,
      secur: this.secure.createUserSecure(),
      form: {query: list, enabled_calls: 1},
    }

    return await this.request.request({
      api: Endpoint.editUserSettings,
      body: params,
    })
  }

  /** @deprecated use authApi instead */
  authWithSocial = async (
    tokenType: AuthType,
    token: string,
  ): Promise<RestResponse<AuthUserResponse>> => {
    const lang = this.storage.language
    const location = this.storage.location
    const sendParams = {
      type_of_reg: tokenType,
      user_key: token,
      lang: lang,
      lat: location?.latitude ?? 0,
      lng: location?.longitude ?? 0,
      app_id: this.storage.platform,
    }

    return await this.request.request<AuthUserResponse>({
      api: Endpoint.createSocialNewUser,
      body: sendParams,
    })
  }
}
